import 'antd/dist/antd.css';

import React, { Suspense } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Switch } from 'react-router-dom';
import { Redirect, Route } from 'react-router';
import { ThemeProvider } from '~/hooks/useTheme';
import { ConfigProvider } from 'antd';
import ptBR from 'antd/lib/locale/pt_BR';
import * as Sentry from '@sentry/react';
import moment from 'moment';
import 'moment/locale/pt-br';

import GlobalStyle from '~/ui/theme/global';
import configurations from '~/store/config';
import { ContractDetailsContextProvider } from '~/contexts/ContractDetailsContext';
import { configureStore } from '~/store';
import Login from './screens/Login/Login';

moment.locale('pt-br');

export const isProduction = () => configurations.endpoint.includes('prod');
export const store = configureStore();

const App = React.lazy(() => import('~/App'));
function DynamicApp() {
  return (
    <Suspense fallback={<div />}>
      <App />
    </Suspense>
  );
}

// Fallback do ErrorBoundary — deliberadamente sem store/tema (se o erro foi
// no boot da árvore, essas dependências podem ser a própria causa).
function ErrorFallback() {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 16,
        fontFamily: 'sans-serif',
      }}
    >
      <h2>Algo deu errado</h2>
      <p>Registramos o problema. Recarregue a página para continuar.</p>
      <button type="button" onClick={() => window.location.reload()}>
        Recarregar
      </button>
      {/* Saída da rota quebrada — recarregar a mesma URL repetiria o erro. */}
      <a href="/backoffice/people">Ir para o início</a>
    </div>
  );
}

// Redirect sem `from` — from= aciona generatePath, incompatível com o
// path-to-regexp 0.1.13 forçado nas resolutions; declarado fora do MainApp
// para não recriar componente a cada render (react/no-unstable-nested-components).
function RedirectToPeople() {
  return <Redirect to="/backoffice/people" />;
}

function MainApp() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <BrowserRouter>
        <ThemeProvider>
          <Provider store={store}>
            <ContractDetailsContextProvider>
              <ConfigProvider locale={ptBR}>
                <Switch>
                  <Route path="/login" component={Login} />
                  <Route path="/backoffice" component={DynamicApp} />
                  <Route path="/" component={RedirectToPeople} />
                </Switch>
                <GlobalStyle />
              </ConfigProvider>
            </ContractDetailsContextProvider>
          </Provider>
        </ThemeProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}

export default Sentry.withProfiler(MainApp);
